import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, XCircle, RefreshCw, Loader2, AlertTriangle,
  Phone, Car, Hash, User, ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  walkInApi, walkInListFromResponse, walkInEffectiveStatus, WALK_IN_STATUS_BADGE,
  type WalkIn, type WalkInEffectiveStatus,
} from '../services/accessApi';
import { pageErrorFromApi } from '../services/planLimitModal';

/**
 * (2026-09-24, bug #105) Guard-scoped "Visitas sin QR" registry UI.
 *
 * The walk-in APIs existed (POST/GET /api/security/walk-ins, approve/deny on
 * /api/walk-ins/:id) but no /security/walk-ins page was routed — the only FE
 * entry points were scanner-integrated (VerifyQR guard list) and host
 * approval cards (MyQR) reachable via notifications. This page wires the
 * SAME existing APIs into a standalone route, reusing the defensive list
 * normalization and badge mapping shared with VerifyQR/MyQR.
 *
 * Decision buttons (approve/deny) hit the unit-member endpoints — backend
 * authorizes (destination-unit members or super_admin); non-authorized
 * callers get the backend rejection surfaced, never a raw axios message.
 */

const TABS: Array<{ key: WalkInEffectiveStatus | 'all'; label: string }> = [
  { key: 'pending_approval', label: 'Pendientes' },
  { key: 'approved', label: 'Aprobadas' },
  { key: 'denied', label: 'Rechazadas' },
  { key: 'expired', label: 'Expiradas' },
  { key: 'timeout_cancelled', label: 'Canceladas (sin aprobación)' },
  { key: 'all', label: 'Todas' },
];

function esDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function SecurityWalkIns() {
  const navigate = useNavigate();
  const { user, activeCondoId, memberships } = useAuth();
  const condoId = activeCondoId || memberships?.[0]?.condoId || user?.condoId || undefined;
  const condoName = memberships?.find((mm) => mm.condoId === condoId)?.condoName;

  const [walkIns, setWalkIns] = useState<WalkIn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<WalkInEffectiveStatus | 'all'>('pending_approval');
  const [decidingId, setDecidingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!condoId) { setWalkIns([]); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await walkInApi.list(condoId);
      setWalkIns(walkInListFromResponse(res.data));
      setError(null);
    } catch (err: any) {
      // 404 = backend without the route → quiet empty state (same policy as VerifyQR).
      if (err?.response?.status === 404) setWalkIns([]);
      else setError(pageErrorFromApi(err, 'Error al cargar visitas sin QR') || 'Error al cargar visitas sin QR');
    } finally {
      setLoading(false);
    }
  }, [condoId]);

  useEffect(() => { void load(); }, [load]);

  const decide = async (w: WalkIn, action: 'approve' | 'deny') => {
    setDecidingId(w.id);
    try {
      await walkInApi[action](w.id);
      await load();
    } catch (err: any) {
      // 409 ALREADY_DECIDED (2026-10-17, ticket #534): expected double-decision
      // guard — another unit member or the 15-min timeout already decided this
      // walk-in (walk-ins/routes.js). Surface the friendly stale-decision
      // message instead of the raw server string (same pattern as
      // Notifications.tsx and WalkInModal.tsx); the raw string here was
      // auto-filing CLIENT_ERROR complaints (#534).
      const msg = String(err?.response?.data?.error || err?.response?.data?.message || '');
      const stale = /already|decided|expirad|timeout|cancel/i.test(msg)
        || err?.response?.status === 409;
      if (stale) {
        setError('Esta solicitud ya fue decidida o expiró.');
      } else {
        const friendly = pageErrorFromApi(err, 'No se pudo registrar la decisión');
        setError(friendly || 'No se pudo registrar la decisión');
      }
    } finally {
      setDecidingId(null);
    }
  };

  const visible = walkIns
    .map((w) => ({ w, eff: walkInEffectiveStatus(w) }))
    .filter(({ eff }) => tab === 'all' || eff === tab);

  return (
    <main className="mx-auto w-full max-w-4xl p-4 md:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <button onClick={() => navigate(-1)} className="mb-2 flex items-center gap-1.5 text-sm text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]">
            <ArrowLeft className="h-4 w-4" /> Volver
          </button>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <ShieldCheck className="h-6 w-6 text-[var(--v-teal-600)]" /> Visitas sin QR
          </h1>
          <p className="text-sm text-[var(--v-text-secondary)]">
            Registro de visitantes registrados sin código QR{condoName ? ` · ${condoName}` : ''}
          </p>
        </div>
        <button onClick={() => void load()} disabled={loading} className="flex items-center gap-2 rounded-xl border border-[var(--v-border-light)] px-3 py-2 text-sm hover:bg-[var(--v-surface-2)]">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-xl bg-[var(--v-danger-bg)] p-3 text-sm text-[var(--v-danger)]">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
        </div>
      )}

      <nav className="mb-4 flex gap-1 overflow-x-auto rounded-xl bg-[var(--v-surface-2)] p-1">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`shrink-0 rounded-lg px-3 py-1.5 text-sm ${tab === t.key ? 'bg-[var(--v-surface-1)] font-semibold shadow-sm' : 'text-[var(--v-text-secondary)]'}`}>
            {t.label}
          </button>
        ))}
      </nav>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[var(--v-teal-600)]" /></div>
      ) : visible.length === 0 ? (
        <p className="rounded-xl border border-[var(--v-border-light)] p-6 text-center text-sm text-[var(--v-text-secondary)]">
          No hay visitas en esta categoría.
        </p>
      ) : (
        <div className="space-y-3">
          {visible.map(({ w, eff }) => {
            const badge = WALK_IN_STATUS_BADGE[eff];
            return (
              <div key={w.id} className="rounded-2xl border border-[var(--v-border-light)] bg-[var(--v-surface-1)] p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    {w.visitor_photo ? (
                      <img src={w.visitor_photo} alt="" className="h-12 w-12 shrink-0 rounded-xl object-cover" />
                    ) : (
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[var(--v-surface-2)]">
                        <User className="h-6 w-6 text-[var(--v-text-secondary)]" />
                      </span>
                    )}
                    <div className="min-w-0">
                      <p className="truncate font-semibold">{w.visitor_name || 'Visitante'}</p>
                      <p className="text-sm text-[var(--v-text-secondary)]">{w.unit_label || 'Unidad —'} · {esDate(w.created_at)}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--v-text-secondary)]">
                        {w.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{w.phone}</span>}
                        {w.plate && <span className="inline-flex items-center gap-1"><Car className="h-3 w-3" />{w.plate}</span>}
                        {w.visitor_id_number && <span className="inline-flex items-center gap-1"><Hash className="h-3 w-3" />{w.visitor_id_number}</span>}
                        {w.access_code && <span className="inline-flex items-center gap-1 font-mono"><User className="h-3 w-3" />{w.access_code}</span>}
                      </div>
                      {w.reason && <p className="mt-1 text-sm text-[var(--v-text-secondary)]">Motivo: {w.reason}</p>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${badge.cls}`}>{badge.label}</span>
                    {eff === 'pending_approval' && (
                      <div className="flex gap-2">
                        <button onClick={() => void decide(w, 'approve')} disabled={decidingId === w.id}
                          className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60">
                          {decidingId === w.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />} Aprobar
                        </button>
                        <button onClick={() => void decide(w, 'deny')} disabled={decidingId === w.id}
                          className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60">
                          {decidingId === w.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />} Rechazar
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
}
